import { useRef, useState } from 'react';
import { Download, FileCheck2, Upload } from 'lucide-react';
import { backupFileName, buildBackup, MAX_BACKUP_BYTES, validateBackupText } from '../services/backup';
import { explainStorageError, PlannerRollbackError } from '../services/db';
import type { BackupCounts, BackupFileV2, BackupPayload, PlannerData, Settings } from '../types';

type Props = {
  appVersion: string;
  data: PlannerData;
  onRestore: (payload: BackupPayload) => Promise<void>;
  settings: Settings;
};

const COUNT_LABELS: ReadonlyArray<[keyof BackupCounts, string]> = [
  ['tasks', 'Tasks'],
  ['taskOccurrences', 'Occurrences'],
  ['events', 'Events'],
  ['notes', 'Notes'],
  ['shoppingItems', 'Shopping'],
  ['categories', 'Categories']
];

const Counts = ({ counts }: { counts: BackupCounts }) => (
  <dl className="backup-counts">
    {COUNT_LABELS.map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{counts[key]}</dd></div>)}
  </dl>
);

const formatDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const restoreError = (caught: unknown) => caught instanceof PlannerRollbackError
  ? `${caught.message} Stop editing and reload the page before trying another recovery action.`
  : `${explainStorageError(caught)} Your previous planner was kept or restored.`;

export const BackupsView = ({ appVersion, data, onRestore, settings }: Props) => {
  const [pendingBackup, setPendingBackup] = useState<BackupFileV2 | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const resetMessages = () => {
    setNotice('');
    setError('');
  };

  const exportBackup = () => {
    resetMessages();
    try {
      const backup = buildBackup(data, settings, appVersion);
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = backupFileName();
      link.click();
      URL.revokeObjectURL(url);
      setNotice('Backup exported. Keep this file somewhere you control.');
    } catch (caught) {
      setError(explainStorageError(caught));
    }
  };

  const readImport = async (file: File | undefined) => {
    if (!file) return;
    resetMessages();
    setPendingBackup(null);
    try {
      if (file.size > MAX_BACKUP_BYTES) throw new Error('The backup is larger than the 10 MiB limit. Your current planner was not changed.');
      const contents = await file.text();
      setPendingBackup(validateBackupText(contents, file.size));
      setNotice('Backup validated locally. Review it before replacing your planner.');
    } catch (caught) {
      setError(explainStorageError(caught));
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const confirmImport = async () => {
    if (!pendingBackup) return;
    if (!window.confirm('Replace every current planner item and preference with this backup? This cannot be merged or undone unless you have another downloaded backup.')) {
      setPendingBackup(null);
      setNotice('Import canceled. Your current planner was not changed.');
      return;
    }
    setBusy(true);
    resetMessages();
    try {
      const { settings: nextSettings, tasks, taskOccurrences, events, notes, shoppingItems, categories } = pendingBackup;
      await onRestore({ settings: nextSettings, tasks, taskOccurrences, events, notes, shoppingItems, categories });
      setPendingBackup(null);
      setNotice('Backup restored. Your planner and preferences were replaced.');
    } catch (caught) {
      setError(restoreError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="backup-page">
      <section className="backup-grid" aria-label="Import and export planner data">
        <article className="settings-card backup-action-card">
          <span className="backup-action-icon" aria-hidden="true"><Download size={26} /></span>
          <div><h2>Export data</h2><p>Download a local JSON copy of your planner and preferences.</p></div>
          <button type="button" className="primary-button" data-backup-action="export" onClick={exportBackup}><Download size={18} />Export backup</button>
        </article>
        <article className="settings-card backup-action-card">
          <span className="backup-action-icon" aria-hidden="true"><Upload size={26} /></span>
          <div><h2>Import data</h2><p>Choose a Spooky Plan JSON backup up to 10 MiB. Validation happens on this device.</p></div>
          <input ref={inputRef} className="sr-only" type="file" accept="application/json,.json" onChange={(event) => void readImport(event.target.files?.[0])} />
          <button type="button" className="secondary-button" data-backup-action="import" disabled={busy} onClick={() => { resetMessages(); inputRef.current?.click(); }}><Upload size={18} />Choose backup</button>
        </article>
      </section>

      {(notice || error) && <div className={error ? 'backup-feedback backup-error' : 'backup-feedback'} role={error ? 'alert' : 'status'}>{error || notice}</div>}

      {pendingBackup && (
        <section className="settings-card import-review" aria-labelledby="import-review-title">
          <div className="import-review-heading"><span className="backup-action-icon" aria-hidden="true"><FileCheck2 size={22} /></span><div><p className="eyebrow">Validated locally</p><h2 id="import-review-title">Review import</h2></div></div>
          <p>Exported {formatDate(pendingBackup.exportedAt)} with app version {pendingBackup.appVersion}. Importing replaces all current planner data.</p>
          <Counts counts={pendingBackup.counts} />
          <div className="backup-confirm-actions">
            <button type="button" className="secondary-button" disabled={busy} onClick={() => { setPendingBackup(null); setNotice('Import canceled. Your current planner was not changed.'); }}>Cancel</button>
            <button type="button" className="primary-button destructive-primary" disabled={busy} onClick={() => void confirmImport()}>{busy ? 'Replacing...' : 'Replace planner'}</button>
          </div>
        </section>
      )}
    </div>
  );
};
